import { useCallback, useMemo, useRef, useState } from "react";

function parseSSEBlock<TData = unknown>(
  block: string,
): SSEMessage<TData> | null {
  const lines = block
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .split("\n")
    .map((l) => l.trimEnd());

  const msg: SSEMessage<TData> = {};
  const dataLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      continue;
    }

    if (trimmed.startsWith(":")) {
      continue;
    }

    const idx = trimmed.indexOf(":");
    const field = idx === -1 ? trimmed : trimmed.slice(0, idx);
    const value = idx === -1 ? "" : trimmed.slice(idx + 1).trimStart();

    switch (field) {
      case "event":
        msg.event = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      // O `id` é a sequência monotônica do run. É ele que voltamos ao
      // servidor em `last_event_id` para retomar de onde paramos — sem isso
      // não existe reconexão, só recomeço.
      case "id":
        msg.id = value;
        break;
      default:
        break;
    }
  }

  if (dataLines.length === 0 && !msg.event && !msg.created_at) {
    return null;
  }

  const raw = dataLines.join("\n");

  if (raw) {
    try {
      msg.data = JSON.parse(raw) as TData;
    } catch {
      msg.data = raw as unknown as TData;
    }
  }

  return msg;
}

export type SSEConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "closed"
  | "detached"
  | "error";

export type SSEMessage<TData = unknown> = {
  id?: string;
  event?: string;
  created_at?: string;
  data?: TData;
};

type StartParams = {
  url: string;
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  body?: unknown;
  /**
   * Não limpa as mensagens já acumuladas ao abrir. Usado na reconexão, em que
   * o replay do servidor emenda no que o cliente já tinha.
   */
  keepMessages?: boolean;
};

export function useSSEStream<TData = unknown>() {
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [status, setStatus] = useState<SSEConnectionStatus>("idle");
  const [messages, setMessages] = useState<Array<SSEMessage<TData>>>([]);
  const [lastEventId, setLastEventId] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    setStatus((s) => (s === "idle" ? "idle" : "closed"));
  }, []);

  /**
   * Solta o transporte sem encerrar o run.
   *
   * É o equivalente web de o app ir para background: o servidor continua
   * gerando, persistindo e bufferizando — só paramos de escutar. Diferente de
   * `stop()` apenas no status resultante, que sinaliza à UI que dá para
   * reconectar em vez de mostrar erro.
   */
  const detach = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    setStatus((s) => (s === "idle" ? "idle" : "detached"));
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    setLastEventId(0);
  }, []);

  const start = useCallback(
    async ({ url, method = "POST", headers, body, keepMessages }: StartParams) => {
      stop();
      setError(null);
      setErrorStatus(null);
      setStatus("connecting");

      if (!keepMessages) {
        setMessages([]);
        setLastEventId(0);
      }

      const abort = new AbortController();

      abortRef.current = abort;

      try {
        const res = await fetch(url, {
          method,
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            ...(method === "POST" ? { "Content-Type": "application/json" } : {}),
            ...(headers ?? {}),
          },
          body: method === "POST" ? JSON.stringify(body) : undefined,
          signal: abort.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");

          setErrorStatus(res.status);

          throw new Error(
            `HTTP ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
          );
        }

        if (!res.body) {
          throw new Error("Response has no body (stream unsupported).");
        }

        setStatus("open");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true }).replaceAll(
            "\r\n",
            "\n",
          );

          while (true) {
            const sep = buffer.indexOf("\n\n");

            if (sep === -1) {
              break;
            }

            const block = buffer.slice(0, sep);

            buffer = buffer.slice(sep + 2);

            const parsed = parseSSEBlock<TData>(block);

            if (parsed) {
              const seq = Number(parsed.id);

              if (Number.isFinite(seq) && seq > 0) {
                setLastEventId(seq);
              }

              setMessages((prev) => [...prev, parsed]);
            }
          }
        }

        setStatus("closed");
      } catch (e) {
        if (abort.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (abortRef.current === abort) {
          abortRef.current = null;
        }
      }
    },
    [stop],
  );

  const lastMessage = useMemo(() => messages[messages.length - 1] ?? null, [
    messages,
  ]);

  return {
    status,
    error,
    errorStatus,
    messages,
    lastMessage,
    lastEventId,
    start,
    stop,
    detach,
    clear,
  };
}
